import {inject, Injectable, signal} from '@angular/core';
import {HttpEvent, HttpEventType, HttpResponse} from '@angular/common/http';
import {MypartHttpService} from '../mypart-http.service';
import {ModalService} from '../modal.service';
import {SlideshowDataService} from './slideshow-data.service';
import {AudioService} from '../audio/audio.service';
import {GamingResponse, hasGamingSongs} from '../models/api-response.models';

@Injectable({
  providedIn: 'root'
})
export class AlternativePromptsService {

  private mypartHttpService = inject(MypartHttpService);
  private modalService = inject(ModalService);
  private slideshowDataService = inject(SlideshowDataService);
  private audioService = inject(AudioService);

  // Loading state for alternative prompts - using signal for reactivity
  private _isLoadingAlternatives = signal<boolean>(false);

  // Snapshot of state when rematch was clicked - for verification
  private _rematchSnapshot: {
    timestamp: string;
    briefDescription: string;
    audioResultsCount: number;
    firstTrackUrl: string | null;
  } | null = null;

  /**
   * Handle request for alternative prompts - main entry point
   */
  handleRequestAlternativePrompt(): void {
    if (this._isLoadingAlternatives()) {
      console.log('⚠️ [ALT-PROMPTS] Rematch already in progress, ignoring duplicate request');
      return;
    }

    console.group('🔄 [ALT-PROMPTS] handleRequestAlternativePrompt() - START');
    console.log('Timestamp:', new Date().toISOString());

    // Log current state BEFORE any changes
    const currentBriefBefore = this.slideshowDataService.briefDescription();
    const currentAudioResults = this.slideshowDataService.audioResults();

    // CAPTURE SNAPSHOT for later verification
    this._rematchSnapshot = {
      timestamp: new Date().toISOString(),
      briefDescription: currentBriefBefore,
      audioResultsCount: currentAudioResults.length,
      firstTrackUrl: currentAudioResults.length > 0 ? currentAudioResults[0]?.url : null
    };
    console.log('📸 [SNAPSHOT CAPTURED] - Will verify changes after operation completes:', this._rematchSnapshot);

    console.log('📊 [STATE BEFORE]:', {
      currentBrief: currentBriefBefore,
      audioResultsCount: currentAudioResults.length,
      isGamingMode: this.slideshowDataService.isGamingMode(),
      gameContext: this.slideshowDataService.gameContext(),
      currentAlternativeIndex: this.slideshowDataService.currentAlternativePromptIndex(),
      isLoadingAlternatives: this._isLoadingAlternatives()
    });

    // Pause current music immediately when refresh is clicked
    if (this.audioService.isPlaying()) {
      console.log('⏸️ Pausing current music for refresh...');
      this.audioService.togglePlayPause();
    }

    // Check if we're in gaming mode
    const isGamingMode = this.slideshowDataService.isGamingMode();
    const gameContext = this.slideshowDataService.gameContext();

    if (isGamingMode && gameContext) {
      console.log('🎮 Gaming mode detected - routing to handleGamingModeRematch()');
      console.log('Game context:', gameContext);
      console.groupEnd();
      this.handleGamingModeRematch(gameContext);
      return;
    }

    console.log('📝 Normal mode - using alternative prompts from stored API response');

    // Normal mode - use alternative prompts
    const response = this.slideshowDataService.fullApiAnalyzeResponse();
    console.log('📡 [STORED API RESPONSE]:', {
      hasResponse: !!response,
      hasData: !!response?.data,
      brief: response?.data?.brief,
      resultsCount: response?.data?.results?.length,
      hasAlternativeBriefs: !!response?.data?.alternative_briefs,
      alternativePromptsCount: response?.data?.alternative_briefs?.prompts?.length
    });

    if (!response?.data?.alternative_briefs?.prompts?.length) {
      console.log('ℹ️ No alternative prompts available in API response - informing user');
      console.log('📄 Alternative briefs structure:', response?.data?.alternative_briefs);
      console.groupEnd();
      this.showNoAlternativesMessage();
      return;
    }

    const prompts = response.data.alternative_briefs.prompts;
    const originalBrief = response.data.brief;
    let currentIndex = this.slideshowDataService.currentAlternativePromptIndex();

    console.log('🎯 [ALTERNATIVE PROMPTS AVAILABLE]:');
    prompts.forEach((p: any, i: number) => {
      console.log(`  [${i}] "${p.music}" ${i === currentIndex ? '← CURRENT INDEX' : ''}`);
    });
    console.log(`📋 Original brief from initial API: "${originalBrief}"`);

    // Find the next alternative that's different from the original brief
    let attempts = 0;
    while (attempts < prompts.length) {
      if (currentIndex >= prompts.length) {
        console.log('🔄 Index exceeded prompts length, restarting from 0');
        currentIndex = 0;
        this.slideshowDataService.setCurrentAlternativePromptIndex(0);
      }

      const currentPrompt = prompts[currentIndex]?.music;

      if (currentPrompt && currentPrompt !== originalBrief) {
        console.log(`🎵 Selected alternative [${currentIndex}]: "${currentPrompt}"`);

        const nextIndex = (currentIndex + 1) % prompts.length;
        console.log(`⏭️ Setting next index to ${nextIndex} for future refresh`);
        this.slideshowDataService.setCurrentAlternativePromptIndex(nextIndex);

        console.groupEnd();
        this.tryAlternativeWithIndex(currentIndex);
        return;
      } else {
        console.log(`⏭️ Skipping [${currentIndex}] (same as original or empty): "${currentPrompt}"`);
        currentIndex = (currentIndex + 1) % prompts.length;
        attempts++;
      }
    }

    console.log('ℹ️ All alternatives are identical to the original brief - informing user');
    console.groupEnd();
    this.showNoAlternativesMessage();
  }

  /**
   * Get current loading state
   */
  isLoadingAlternatives(): boolean {
    return this._isLoadingAlternatives();
  }

  /**
   * Reset the service state
   */
  reset(): void {
    this._isLoadingAlternatives.set(false);
    this._rematchSnapshot = null;
  }

  /**
   * Verify that state actually changed after rematch operation
   * Call this at the END of any rematch operation to confirm changes
   */
  private verifyRematchChanges(operationType: string): void {
    if (!this._rematchSnapshot) {
      console.warn('⚠️ [VERIFY] No snapshot available - cannot verify changes');
      return;
    }

    const currentBrief = this.slideshowDataService.briefDescription();
    const currentResults = this.slideshowDataService.audioResults();
    const currentFirstTrackUrl = currentResults.length > 0 ? currentResults[0]?.url : null;

    const briefChanged = currentBrief !== this._rematchSnapshot.briefDescription;
    const resultsCountChanged = currentResults.length !== this._rematchSnapshot.audioResultsCount;
    const tracksChanged = currentFirstTrackUrl !== this._rematchSnapshot.firstTrackUrl;

    const anyChange = briefChanged || resultsCountChanged || tracksChanged;

    console.group(`🔍 [VERIFY REMATCH CHANGES] - ${operationType}`);
    console.log('Snapshot timestamp:', this._rematchSnapshot.timestamp);
    console.log('Verification timestamp:', new Date().toISOString());

    console.log('📊 COMPARISON:', {
      brief: {
        before: this._rematchSnapshot.briefDescription,
        after: currentBrief,
        changed: briefChanged
      },
      resultsCount: {
        before: this._rematchSnapshot.audioResultsCount,
        after: currentResults.length,
        changed: resultsCountChanged
      },
      firstTrackUrl: {
        before: this._rematchSnapshot.firstTrackUrl?.substring(0, 50) + '...',
        after: currentFirstTrackUrl?.substring(0, 50) + '...',
        changed: tracksChanged
      }
    });

    if (anyChange) {
      console.log('✅ [SUCCESS] State DID change after rematch:', {
        briefChanged,
        resultsCountChanged,
        tracksChanged
      });
    } else {
      console.error('❌ [PROBLEM] State did NOT change after rematch!');
      console.error('🚨 User will see the SAME content as before clicking rematch!');
      console.error('Debug info:', {
        operationType,
        snapshot: this._rematchSnapshot,
        current: {
          brief: currentBrief,
          resultsCount: currentResults.length,
          firstTrackUrl: currentFirstTrackUrl
        }
      });
    }

    console.groupEnd();

    // Clear snapshot after verification
    this._rematchSnapshot = null;
  }

  /**
   * Try alternative prompts from the API response
   */
  private tryAlternativePrompts(): void {
    const response = this.slideshowDataService.fullApiAnalyzeResponse();

    if (!response?.data?.alternative_briefs?.prompts?.length) {
      console.log('❌ No alternative prompts available in tryAlternativePrompts, gracefully falling back');
      this.revertToOriginalResults();
      return;
    }

    const prompts = response.data.alternative_briefs.prompts;
    const currentIndex = this.slideshowDataService.currentAlternativePromptIndex();

    console.log(`Found ${prompts.length} alternative prompts, trying index ${currentIndex}`);

    if (currentIndex < prompts.length) {
      const currentPrompt = prompts[currentIndex]?.music;
      if (currentPrompt) {
        console.log(`Trying alternative prompt ${currentIndex + 1}/${prompts.length}: "${currentPrompt}"`);
        this.searchWithQuery(currentPrompt);

        // Move to next prompt for subsequent requests
        this.slideshowDataService.setCurrentAlternativePromptIndex(currentIndex + 1);
      } else {
        console.log('❌ No prompt found at current index in tryAlternativePrompts, gracefully falling back');
        this.revertToOriginalResults();
      }
    } else {
      // We've exhausted all alternative prompts - revert to original results
      console.log('All alternative prompts have been tried, reverting to original results');
      this.revertToOriginalResults();
    }
  }

  /**
   * Try alternative prompt at specific index
   */
  private tryAlternativeWithIndex(index: number): void {
    console.group('🎯 [ALT-PROMPTS] tryAlternativeWithIndex() - START');
    console.log('Timestamp:', new Date().toISOString());
    console.log('Requested index:', index);

    const response = this.slideshowDataService.fullApiAnalyzeResponse();

    if (!response?.data?.alternative_briefs?.prompts?.length) {
      console.log('❌ No alternative prompts available, falling back to original');
      console.groupEnd();
      this.revertToOriginalResults();
      return;
    }

    const prompts = response.data.alternative_briefs.prompts;
    console.log('📋 Available prompts:', prompts.length);

    if (index < prompts.length) {
      const currentPrompt = prompts[index]?.music;
      if (currentPrompt) {
        console.log(`🎵 Using prompt at index [${index}]: "${currentPrompt}"`);
        console.log('➡️ Calling searchWithQuery()...');
        console.groupEnd();
        this.searchWithQuery(currentPrompt);
      } else {
        console.log(`⚠️ No prompt found at index ${index}`);
        console.groupEnd();
        this.revertToOriginalResults();
      }
    } else {
      console.log(`❌ Index ${index} out of range (max: ${prompts.length - 1})`);
      console.groupEnd();
      this.revertToOriginalResults();
    }
  }

  /**
   * Search for music using a text query
   */
  private searchWithQuery(query: string): void {
    console.group('🔍 [ALT-PROMPTS] searchWithQuery() - START');
    console.log('Timestamp:', new Date().toISOString());
    console.log('Query:', query);

    if (!query) {
      console.error('❌ No search query provided');
      console.groupEnd();
      return;
    }

    // Log state before API call
    const briefBefore = this.slideshowDataService.briefDescription();
    console.log('📊 [STATE BEFORE API CALL]:', {
      currentBrief: briefBefore,
      query: query,
      willSetBriefTo: query
    });

    console.log('⏳ Setting loading state to TRUE');
    this._isLoadingAlternatives.set(true);

    console.log('📡 Calling refreshSearch API with:', { query, numResults: 10, searchResolution: 'section' });

    this.mypartHttpService.refreshSearch(query, 10, 'section').subscribe({
      next: (event: HttpEvent<any>) => {
        if (event.type === HttpEventType.Response) {
          const response = (event as HttpResponse<any>).body;
          console.log('✅ [API RESPONSE RECEIVED]:', {
            status: response?.status,
            hasData: !!response?.data,
            resultsCount: response?.data?.results?.length,
            hasBrief: !!response?.data?.brief,
            apiBrief: response?.data?.brief
          });

          this._isLoadingAlternatives.set(false);

          if (response?.data?.results && response.data.results.length > 0) {
            console.log(`🎉 Alternative results found: ${response.data.results.length} tracks`);

            // Log the description update in detail
            const briefBeforeUpdate = this.slideshowDataService.briefDescription();
            console.log('📝 [UPDATING BRIEF DESCRIPTION]:', {
              briefBeforeUpdate: briefBeforeUpdate,
              apiBrief: response?.data?.brief || '(not returned by API)',
              queryUsedAsBrief: query,
              willSetTo: query,
              reason: 'refreshSearch API does NOT return brief field - using query as description'
            });

            this.slideshowDataService.setAudioResults(response.data.results);
            this.slideshowDataService.setBriefDescription(query);
            this.slideshowDataService.setResultsAvailable(true);

            // Verify the update
            const briefAfterUpdate = this.slideshowDataService.briefDescription();
            console.log('✅ [STATE AFTER UPDATE]:', {
              briefAfterUpdate: briefAfterUpdate,
              briefChanged: briefBeforeUpdate !== briefAfterUpdate,
              newResultsCount: this.slideshowDataService.audioResults().length
            });

            console.groupEnd();

            // VERIFY against original snapshot
            this.verifyRematchChanges('Normal Mode - Alternative Prompt Search');
          } else {
            console.log('⚠️ No results found in API response, trying next alternative...');
            console.groupEnd();
            this.tryAlternativePrompts();
          }
        }
      },
      error: (error: any) => {
        console.error('❌ Search error:', error);
        console.log('💥 Error occurred, setting loading state to FALSE');
        this._isLoadingAlternatives.set(false);
        console.groupEnd();
        this.tryAlternativePrompts();
      }
    });
  }

  /**
   * Revert to original results when all alternative prompts are exhausted
   */
  private revertToOriginalResults(): void {
    console.group('🔙 [ALT-PROMPTS] revertToOriginalResults() - START');
    console.log('Timestamp:', new Date().toISOString());

    const response = this.slideshowDataService.fullApiAnalyzeResponse();

    if (!response?.data?.results || !response?.data?.brief) {
      console.warn('❌ No original results to revert to');
      this.modalService.showNoMatches();
      this._rematchSnapshot = null;
      console.groupEnd();
      return;
    }

    const currentBrief = this.slideshowDataService.briefDescription();
    const currentResults = this.slideshowDataService.audioResults();
    const alreadyAtOriginal =
      currentBrief === response.data.brief &&
      currentResults.length === response.data.results.length &&
      currentResults.every((track: any, i: number) => track?.url === response.data.results[i]?.url);

    // Skip the write when nothing would change - otherwise the slide-show audio-init effect sees identical URLs and never recreates the Howl, silently breaking Play.
    if (alreadyAtOriginal) {
      console.log('ℹ️ Already on original brief/tracks - skipping no-op revert');
      console.groupEnd();
      this.showNoAlternativesMessage();
      return;
    }

    console.log('📝 [REVERTING TO ORIGINAL]:', {
      fromBrief: currentBrief,
      toBrief: response.data.brief,
      resultsCount: response.data.results.length
    });

    this.slideshowDataService.setAudioResults(response.data.results);
    this.slideshowDataService.setBriefDescription(response.data.brief);
    this.slideshowDataService.setCurrentAlternativePromptIndex(0);

    console.groupEnd();
    this.showNoAlternativesMessage();
  }

  // Called whenever a rematch has no usable alternative (no prompts, all identical to original, or revert-to-original path). Uses showNoMatches for semantic parity with other "no music found" flows in this codebase.
  private showNoAlternativesMessage(): void {
    this.modalService.showNoMatches({
      message: 'No alternative matches available for this content. Try uploading a different image or video.'
    });
    this._rematchSnapshot = null;
  }

  /**
   * Handle gaming mode rematch by calling contextual API again
   */
  private handleGamingModeRematch(gameContext: string): void {
    console.group('🎮 [ALT-PROMPTS] handleGamingModeRematch() - START');
    console.log('Timestamp:', new Date().toISOString());
    console.log('Game context:', gameContext);

    // Log current state
    const currentBrief = this.slideshowDataService.briefDescription();
    const currentResults = this.slideshowDataService.audioResults();
    console.log('📊 [STATE BEFORE GAMING REMATCH]:', {
      currentBrief: currentBrief,
      audioResultsCount: currentResults.length,
      gameContext: gameContext,
      isGamingMode: this.slideshowDataService.isGamingMode()
    });

    this._isLoadingAlternatives.set(true);

    const slideshowImages = this.slideshowDataService.slideshowImages();
    const slideshowMediaItems = this.slideshowDataService.slideshowMediaItems();
    console.log('📷 [AVAILABLE MEDIA]:', {
      slideshowImagesCount: slideshowImages.length,
      slideshowImages: slideshowImages,
      mediaItemsCount: slideshowMediaItems.length
    });

    if (!slideshowImages.length) {
      console.error('❌ No slideshow images available for rematch');
      this._isLoadingAlternatives.set(false);
      this.modalService.showErrorMessage({ message: 'No media available for rematch' });
      console.groupEnd();
      return;
    }

    console.warn('⚠️ [GAMING MODE REMATCH NOT IMPLEMENTED]');
    console.log('This function is currently a STUB - it does NOT call the contextual API');
    console.log('Instead, it just appends text to the current brief after a 1.5s delay');

    setTimeout(() => {
      this._isLoadingAlternatives.set(false);

      const briefBefore = this.slideshowDataService.briefDescription();
      const newBrief = briefBefore + ' (Rematch - New recommendations)';

      console.log('📝 [UPDATING BRIEF - STUB IMPLEMENTATION]:', {
        briefBefore: briefBefore,
        newBrief: newBrief,
        reason: 'STUB: Just appending text, NOT calling API'
      });

      this.slideshowDataService.setBriefDescription(newBrief);

      const briefAfter = this.slideshowDataService.briefDescription();
      console.log('✅ [STATE AFTER GAMING REMATCH STUB]:', {
        briefAfter: briefAfter,
        briefChanged: briefBefore !== briefAfter,
        audioResultsCount: this.slideshowDataService.audioResults().length,
        note: 'Audio results NOT changed - this is a stub!'
      });

      console.log('💡 TO FIX: Need to call mypartHttpService.searchContextualImage(file, textPrompt, gameContext)');
      console.groupEnd();

      // VERIFY against original snapshot
      this.verifyRematchChanges('Gaming Mode - STUB (Not Implemented)');
    }, 1500);
  }
}
